import { formatDate, t } from '../i18n';
import type { DateRange, DateRangePreset } from '../types/analytics';

const reportingTimezone = import.meta.env.VITE_ANALYTICS_REPORTING_TIMEZONE || 'Europe/Belgrade';

function localIsoDate(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** The current calendar date in the reporting timezone (YYYY-MM-DD). It is the latest date a range may end on. */
export function todayInReportingTimezone(now = new Date(), timezone = reportingTimezone): string {
  return localIsoDate(now, timezone);
}

/** Adds whole days to a YYYY-MM-DD date. Noon UTC keeps the arithmetic clear of any DST edge. */
export function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function rangeFromPreset(preset: Exclude<DateRangePreset, 'custom'>, now = new Date(), timezone = reportingTimezone): DateRange {
  const endText = localIsoDate(now, timezone);
  if (preset === 'yesterday') {
    const day = addDays(endText, -1);
    return { preset, start: day, end: day, timezone };
  }
  const start = new Date(`${endText}T12:00:00Z`);
  if (preset === '7d') start.setUTCDate(start.getUTCDate() - 6);
  if (preset === '30d') start.setUTCDate(start.getUTCDate() - 29);
  return { preset, start: start.toISOString().slice(0, 10), end: endText, timezone };
}

export function rangeDays(range: DateRange): number {
  return Math.max(1, Math.round((Date.parse(range.end) - Date.parse(range.start)) / 86_400_000) + 1);
}

export function formatRange(range: DateRange): string {
  if (range.preset === 'today') return t('v1.range.today');
  if (range.preset === 'yesterday') return t('v1.range.yesterday');
  if (range.preset === '7d') return t('v1.range.7d');
  if (range.preset === '30d') return t('v1.range.30d');
  return t('v1.range.span', { start: formatDate(range.start), end: formatDate(range.end) });
}

export type CompareMode = 'off' | 'previous' | 'week';

/** Comparison captions in the active language (read when shown, so a language switch applies). */
export const compareLabels: Record<Exclude<CompareMode, 'off'>, string> = {
  get previous() { return t('v1.compare.previous'); },
  get week() { return t('v1.compare.week'); },
};

/**
 * The range to compare against, or null when comparison is off.
 * `previous` is the period of the same length immediately before; `week` is the same days shifted back seven days.
 */
export function compareRange(range: DateRange, mode: CompareMode): DateRange | null {
  if (mode === 'off') return null;
  if (mode === 'week') return { preset: 'custom', start: addDays(range.start, -7), end: addDays(range.end, -7), timezone: range.timezone };
  const days = rangeDays(range);
  const end = addDays(range.start, -1);
  return { preset: 'custom', start: addDays(end, -(days - 1)), end, timezone: range.timezone };
}

/** Says which of the provisional dates are today and yesterday, in plain words. */
export function describeProvisionalDates(dates: string[], today = todayInReportingTimezone()): string {
  const yesterday = addDays(today, -1);
  const words = dates.map((date) => (date === today ? t('v1.provisional.today') : date === yesterday ? t('v1.provisional.yesterday') : formatDate(date)));
  if (words.length === 0) return '';
  if (words.length === 1) return words[0]!;
  return `${words.slice(0, -1).join(', ')} ${t('v1.provisional.and')} ${words.at(-1)}`;
}

const REASONS = ['in_progress', 'daily_export_pending', 'daily_export_missing', 'adjacent_table'] as const;

/** One sentence per reason, from the API's `provisionalDays`. Empty when the API gave no reasons. */
export function describeProvisionalReasons(days: Array<{ date: string; reason: string; table: string }>, today = todayInReportingTimezone()): string[] {
  const groups = new Map<string, Array<{ date: string; table: string }>>();
  for (const day of days) groups.set(day.reason, [...(groups.get(day.reason) ?? []), day]);
  return REASONS
    .filter((reason) => groups.has(reason))
    .map((reason) => {
      const group = groups.get(reason)!;
      const dates = describeProvisionalDates(group.map((day) => day.date), today);
      const tables = describeProvisionalDates([...new Set(group.map((day) => day.table))], today);
      const sentence = t(`v1.provisional.${reason}`, { dates, tables });
      return sentence.charAt(0).toUpperCase() + sentence.slice(1);
    });
}
